import { Card } from "@/components/ui/card";
import { ProductSection } from "@/components/product/product-section";

export default function Terms() {
  return (
    <div className="min-h-screen">
      <section className="py-16 pt-24">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto p-8">
            <h1 className="text-3xl font-bold mb-8">Terms & Conditions</h1>
            <div className="max-w-none space-y-12">
              <p className="text-lg">
                Thank you for considering using our products. Please read these Terms and our Privacy Policy carefully before using liquidstretch.com (the "Site").
              </p>

              {/* Overview */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Overview</h2>
                <p className="text-base leading-relaxed mb-4">
                  This website is operated by Liquid Stretch. Throughout the site, the terms "we", "us" and "our" refer to Liquid Stretch. Liquid Stretch offers this website, including all information, tools and services available from this site to you, the user, conditioned upon your acceptance of all terms, conditions, policies and notices stated here.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  By visiting our site and/or purchasing something from us, you engage in our "Service" and agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions of this agreement, then you may not access the website or use any services.
                </p>
                <p className="text-base leading-relaxed">
                  We reserve the right to update, change or replace any part of these Terms by posting updates on our website. It is your responsibility to check this page periodically for changes. Your continued use of the website following the posting of any changes constitutes acceptance of those changes.
                </p>
              </div>

              {/* Privacy Policy */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Privacy Policy</h2>
                <p className="text-base leading-relaxed mb-4">
                  This Privacy Policy describes how your personal information is collected, used, and shared when you visit liquidstretch.com, join a harvest availability list, or make a purchase.
                </p>

                <h3 className="text-lg font-semibold mb-3">Personal Information We Collect</h3>
                <p className="text-base leading-relaxed mb-4">
                  When you visit the Site, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. Additionally, as you browse the Site, we collect information about the individual web pages or products that you view, what websites or search terms referred you to the Site, and information about how you interact with the Site. We refer to this automatically-collected information as "Device Information".
                </p>
                <p className="text-base leading-relaxed mb-4">
                  We collect Device Information using technologies such as cookies, log files, and web beacons. Cookies are data files placed on your device; log files track actions on the Site including IP address, browser type, and date/time stamps.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  When you make a purchase or attempt to make a purchase through the Site, we collect certain information from you, including your name, billing address, shipping address, payment information (including credit card numbers), email address, and phone number. We refer to this information as "Order Information".
                </p>
                <p className="text-base leading-relaxed mb-4">
                  When you join the next-batch list, we collect your email address and any optional name and quantity-interest information you provide. We refer to this as "Harvest Interest Information". Joining the list does not place an order or take payment.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  When we talk about "Personal Information" in this Privacy Policy, we mean Device Information, Order Information, and Harvest Interest Information.
                </p>

                <h3 className="text-lg font-semibold mb-3">How We Use Your Personal Information</h3>
                <p className="text-base leading-relaxed mb-4">
                  We use Order Information to fulfill orders (including processing payments, arranging shipping, and providing confirmations), communicate with you, and screen orders for potential risk or fraud. We use Harvest Interest Information to send the Golden Hour Harvest availability notice and understand likely demand. We use Device Information to help screen for risk and fraud and to improve and optimize the Site, including through analytics about how visitors browse and interact with it.
                </p>

                <h3 className="text-lg font-semibold mb-3">Sharing Your Personal Information</h3>
                <p className="text-base leading-relaxed mb-4">
                  We share Personal Information with service providers only as needed for the purposes described above. Netlify hosts the Site and processes next-batch form submissions. Snipcart powers the online store and checkout. We also use Google Analytics to understand how visitors use the Site. We may share Personal Information to comply with applicable laws and regulations, respond to a lawful request, or protect our rights.
                </p>

                <h3 className="text-lg font-semibold mb-3">Data Retention</h3>
                <p className="text-base leading-relaxed mb-4">
                  We maintain Order Information for our records unless and until you ask us to delete it. We retain Harvest Interest Information until the availability notice has been sent or until you ask us to remove it, whichever comes first.
                </p>

                <h3 className="text-lg font-semibold mb-3">Your Rights</h3>
                <p className="text-base leading-relaxed mb-4">
                  You have the right to access personal information we hold about you and to ask that your personal information be corrected, updated, or deleted. If you would like to exercise this right, please contact us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a>.
                </p>

                <h3 className="text-lg font-semibold mb-3">Age Restrictions</h3>
                <p className="text-base leading-relaxed mb-4">
                  The Site is not intended for individuals under the age of 21. By using this site, you represent that you are at least the age of majority in your state or province of residence.
                </p>

                <h3 className="text-lg font-semibold mb-3">Contact Us</h3>
                <p className="text-base leading-relaxed">
                  For more information about our privacy practices, contact us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a>.
                </p>
              </div>

              {/* Website Disclaimer */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Website Disclaimer</h2>
                <p className="text-base leading-relaxed mb-4">
                  This website contains general information about diet, health, and nutrition. None of the information is advice, and should not be considered or treated as a substitute for advice from a healthcare professional. The contents of this website are for informational purposes only and are not intended to be a substitute for professional medical advice, diagnosis or treatment. Always seek the advice of your physician or other qualified health professional with any questions you may have regarding a medical condition.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  You must not rely on the information on this website as an alternative to medical advice from your doctor or other professional healthcare provider. If you think you may be suffering from any medical condition you should seek medical attention. Information and statements regarding dietary supplements have not been evaluated by the Food and Drug Administration and are not intended to diagnose, treat, cure or prevent any disease.
                </p>
              </div>

              {/* Online Store Terms */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Online Store Terms</h2>
                <p className="text-base leading-relaxed mb-4">
                  By agreeing to these Terms of Service, you represent that you are at least the age of majority in your state or province of residence. You may not use our products for any illegal or unauthorized purpose nor may you, in the use of the Service, violate any laws in your jurisdiction. You must not transmit any worms or viruses or any code of a destructive nature.
                </p>
              </div>

              {/* General Conditions */}
              <div>
                <h2 className="text-2xl font-bold mb-6">General Conditions</h2>
                <p className="text-base leading-relaxed">
                  We reserve the right to refuse service to anyone for any reason at any time. You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion of the Service without express written permission by us.
                </p>
              </div>

              {/* Shipping */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Shipping</h2>
                <p className="text-base leading-relaxed">
                  Please allow up to 10 days for your order to arrive. If more than 10 days have passed, please contact us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a> to review the status of your order. Refunds will be handled through email if necessary.
                </p>
              </div>

              {/* Refunds & Returns */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Refunds & Returns</h2>
                <p className="text-base leading-relaxed mb-4">
                  Within 30 days of receiving your order, if you are not completely satisfied with your purchase, send the remaining product in its original packaging back to 169 Bailey Hill Road, Readsboro, Vermont, 05350. The purchaser is responsible for the costs of return shipping.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  We will refund your purchase price and a credit will automatically be applied to your original method of payment.
                </p>
                <p className="text-base leading-relaxed">
                  To follow-up on the status of your return, please contact us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a>.
                </p>
              </div>

              {/* Exchanges */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Exchanges</h2>
                <p className="text-base leading-relaxed mb-4">
                  We exchange goods only if the packaging is damaged during shipping. In circumstances where you consider that a product is damaged, simply send a picture of the damaged product to us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a> with details of the product and the defect.
                </p>
                <p className="text-base leading-relaxed">
                  If we determine that your product was damaged during shipping, we will send you a replacement product.
                </p>
              </div>

              {/* Products */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Products</h2>
                <p className="text-base leading-relaxed mb-4">
                  Certain products may be available exclusively online through the website. These products are subject to return or exchange only according to our Return Policy. We reserve the right to limit the sales of our products to any person, geographic region or jurisdiction. We reserve the right to limit the quantities of any products we offer. All descriptions of products or product pricing are subject to change at any time without notice, at our sole discretion.
                </p>
              </div>

              {/* Prohibited Uses */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Prohibited Uses</h2>
                <p className="text-base leading-relaxed mb-4">
                  In addition to other prohibitions as set forth in these Terms, you are prohibited from using the site or its content: (a) for any unlawful purpose; (b) to solicit others to perform or participate in any unlawful acts; (c) to violate any regulations, rules, laws, or local ordinances; (d) to infringe upon or violate our intellectual property rights or the intellectual property rights of others; (e) to harass, abuse, insult, harm, defame, or discriminate; (f) to submit false or misleading information; (g) to upload or transmit viruses or any other type of malicious code; (h) to collect or track the personal information of others; or (i) to spam, phish, or scrape.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Limitation of Liability</h2>
                <p className="text-base leading-relaxed">
                  We do not guarantee that your use of our service will be uninterrupted, timely, secure or error-free. The service and all products delivered through the service are provided "as is" and "as available" without any warranties. In no case shall Liquid Stretch be liable for any injury, loss, claim, or any direct, indirect, incidental, punitive, special, or consequential damages of any kind arising from your use of the service or any products procured using the service.
                </p>
              </div>

              {/* Governing Law & Contact */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Governing Law & Contact</h2>
                <p className="text-base leading-relaxed mb-4">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the United States.
                </p>
                <p className="text-base leading-relaxed">
                  Questions about the Terms of Service should be sent to us at{" "}
                  <a href="mailto:help@liquidstretch.com" className="text-primary hover:underline">help@liquidstretch.com</a>.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <ProductSection />
    </div>
  );
}
